import type { SubmitHandler } from "react-hook-form";

import { useEffect } from "react";
import { Button } from "src/components/ui/button";
import { useForm, useFormState, useWatch } from "react-hook-form";
import { ObservationTypePicker } from "src/features/observationsAdmin/components/ObservationTypePicker";
import { FormRecipients } from "src/features/observationsAdmin/components/FormRecipients";
import { useNewObservationFormHandler } from "src/features/observationsAdmin/hooks/useNewObservationFormHandler";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, Schema } from "./schema";
import { Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import LockIcon from "src/assets/icon/lock.svg?react";
import { ObservationCategoryTypePicker } from "src/features/Admin/ObservationsCategories/components/ObservationCategoryTypePicker";
import { Input } from "src/components/ui/input";
import { RadioGroup, RadioGroupItem } from "src/components/ui/radio-group";

interface CreateNewObservationFormProps {
	className?: string;
	onValid: SubmitHandler<Schema>;

	defaultValues: Schema;

	isObservationTypeEditable?: boolean;
}

const CreateNewObservationForm = ({
	onValid,
	defaultValues,
	isObservationTypeEditable = true,
	className
}: CreateNewObservationFormProps) => {
	const { control, setValue, handleSubmit, reset } = useForm<Schema>({
		defaultValues,
		resolver: zodResolver(schema),
		mode: "onChange",
		reValidateMode: "onChange"
	});

	const {
		onObservationTypeChange,
		onNameChange,
		onCompletionSettingsChange,
		onGradingRecipientChange,
		onGradingEmailChange,
		onObservationEmailChange,
		onObservationRecipientChange,
		onCompletionCustomEmailSettingsChange,
		onCustomEmailSubjectChange,
		onCustomEmailTitleChange,
		onCustomEmailBodyChange,
		onObserverAccessChange,
		onCategoryChange
	} = useNewObservationFormHandler({
		setValue
	});

	const { errors, isValid } = useFormState({ control });

	const formName = useWatch({ control, name: "formName" });
	const observationType = useWatch({ control, name: "observationType" });
	const category = useWatch({ control, name: "category" });
	const sendEmail = useWatch({ control, name: "sendEmail" });
	const limitToCurrentUser = useWatch({ control, name: "limitToCurrentUser" });

	const customEmail = useWatch({ control, name: "customEmail" });
	const customEmailTitle = useWatch({ control, name: "customEmailTitle" });
	const customEmailSubject = useWatch({ control, name: "customEmailSubject" });
	const customEmailBody = useWatch({ control, name: "customEmailBody" });

	const observationRecipients = useWatch({
		control,
		name: "observationRecipients"
	});
	const gradingRecipients = useWatch({ control, name: "gradingRecipients" });

	const observationSpecificEmailAddress = useWatch({
		control,
		name: "observationSpecificEmailAddress"
	});

	const gradingSpecificEmailAddress = useWatch({
		control,
		name: "gradingSpecificEmailAddress"
	});

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues]);

	const isGreatConversationForm = observationType === "GREAT_CONVERSATION";

	const observationsHeading = isGreatConversationForm
		? "Observations"
		: undefined;

	return (
		<div className={className}>
			<form
				className="pb-8"
				onKeyDown={(e) => {
					if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
						e.preventDefault();
					}
				}}
			>
				<div className="flex flex-col gap-4">
					{/* Form type */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.observationType ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.observationType && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.observationType.message ||
										"Please select a form type"}
								</span>
							</div>
						)}
						<div className="p-6">
							<ObservationTypePicker
								label="Which type of form do you want to create?"
								helperText={
									<p className="text-[13px] text-muted-foreground mb-4">
										Once you save and continue you will not be able to change
										the form type.
									</p>
								}
								placeholder="Please select"
								value={observationType}
								onChange={onObservationTypeChange}
								defaultValue={defaultValues?.observationType}
								isEditable={isObservationTypeEditable}
							/>
						</div>
					</div>

					{/* Form name */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.formName ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.formName && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.formName.message || "Please enter a form name"}
								</span>
							</div>
						)}
						<div className="p-6">
							<label
								htmlFor="form-name-input"
								className="font-heading text-lg font-medium text-foreground mb-4 block"
							>
								Form name
							</label>
							<Input
								id="form-name-input"
								placeholder="Enter form name"
								value={formName}
								onChange={onNameChange}
								className="w-full bg-white"
							/>
						</div>
					</div>

					{/* Form category */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.category ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.category && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.category.message || "Please select a category"}
								</span>
							</div>
						)}
						<div className="p-6">
							<ObservationCategoryTypePicker
								label="Form category"
								placeholder="Please select"
								value={category}
								onChange={onCategoryChange}
							/>
						</div>
					</div>

					{/* Observer access */}
					{(observationType === "GREAT_CONVERSATION" ||
						observationType === "INDIVIDUAL") && (
						<div className="rounded-xl border border-border bg-subtle shadow-sm">
							<div className="p-6">
								<label className="font-heading text-lg font-medium text-foreground mb-4 block">
									Observer access
								</label>

								<RadioGroup
									value={limitToCurrentUser ? "true" : "false"}
									onValueChange={onObserverAccessChange}
									className="flex flex-col gap-3"
								>
									<div className="flex items-center gap-2">
										<RadioGroupItem value="false" id="access-others" />
										<label
											htmlFor="access-others"
											className="text-sm cursor-pointer"
										>
											Form can be completed on behalf of others
										</label>
									</div>
									<div className="flex items-center gap-2">
										<RadioGroupItem value="true" id="access-self" />
										<label
											htmlFor="access-self"
											className="text-sm cursor-pointer"
										>
											Form can be completed by the single logged in user only
										</label>
									</div>
								</RadioGroup>
							</div>
						</div>
					)}

					{/* Form completion settings */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Form completion settings
							</label>

							<RadioGroup
								value={sendEmail ? "send" : "dont-send"}
								onValueChange={onCompletionSettingsChange}
								className="flex flex-col gap-3 mb-4"
							>
								<div className="flex items-center gap-2">
									<RadioGroupItem value="send" id="email-send" />
									<label
										htmlFor="email-send"
										className="text-sm cursor-pointer"
									>
										Upon completion send emails to:
									</label>
								</div>
								<div className="flex items-center gap-2">
									<RadioGroupItem value="dont-send" id="email-dont" />
									<label
										htmlFor="email-dont"
										className="text-sm cursor-pointer"
									>
										Do not send any emails
									</label>
								</div>
							</RadioGroup>

							<hr className="border-border mb-4" />

							<div>
								{sendEmail && !observationType && (
									<p className="text-[13px] text-muted-foreground text-center">
										Please select a form type to see available options
									</p>
								)}

								{!sendEmail && (
									<p className="text-[13px] text-muted-foreground text-center">
										No emails will be sent when this form has been completed
									</p>
								)}

								{sendEmail && observationType && (
									<div className="flex justify-between items-start gap-4">
										<FormRecipients
											observationType={observationType}
											defaultValue={observationRecipients ?? []}
											onCheckboxGroupChange={onObservationRecipientChange}
											isCheckboxGroupInvalid={Boolean(
												errors.observationRecipients
											)}
											isEmailRecipientInvalid={Boolean(
												errors.observationSpecificEmailAddress
											)}
											emailRecipientErrorMessage={
												errors.observationSpecificEmailAddress?.message
											}
											checkboxErrorMessage={
												errors.observationRecipients?.message
											}
											recipients={observationRecipients ?? []}
											heading={observationsHeading}
											specificEmailAddress={
												observationSpecificEmailAddress ?? ""
											}
											onSpecificEmailAddressChange={onObservationEmailChange}
											width="100%"
											data-testid="observation-recipients"
										/>

										{isGreatConversationForm && (
											<FormRecipients
												observationType={observationType}
												defaultValue={gradingRecipients ?? []}
												onCheckboxGroupChange={onGradingRecipientChange}
												isCheckboxGroupInvalid={Boolean(
													errors.gradingRecipients
												)}
												isEmailRecipientInvalid={Boolean(
													errors.gradingSpecificEmailAddress
												)}
												emailRecipientErrorMessage={
													errors.gradingSpecificEmailAddress?.message
												}
												checkboxErrorMessage={errors.gradingRecipients?.message}
												recipients={gradingRecipients ?? []}
												heading="Grading"
												specificEmailAddress={gradingSpecificEmailAddress ?? ""}
												onSpecificEmailAddressChange={onGradingEmailChange}
												width="100%"
												data-testid="grading-recipients"
											/>
										)}
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Email content */}
					{sendEmail && (
						<div className="rounded-xl border border-border bg-subtle shadow-sm">
							<div className="p-6">
								<label className="font-heading text-lg font-medium text-foreground mb-4 block">
									Email content
								</label>

								<RadioGroup
									value={customEmail ? "true" : "false"}
									onValueChange={onCompletionCustomEmailSettingsChange}
									className="flex flex-col gap-3 mb-4"
								>
									<div className="flex items-center gap-2">
										<RadioGroupItem value="false" id="email-default" />
										<label
											htmlFor="email-default"
											className="text-sm cursor-pointer"
										>
											Use default email content
										</label>
									</div>
									<div className="flex items-center gap-2">
										<RadioGroupItem value="true" id="email-custom" />
										<label
											htmlFor="email-custom"
											className="text-sm cursor-pointer"
										>
											Create new email content
										</label>
									</div>
								</RadioGroup>

								<hr className="border-border mb-4" />

								{customEmail && (
									<div className="flex flex-col gap-5">
										<div>
											<label
												htmlFor="custom-email-title-input"
												className="text-sm font-medium text-zinc-600 mb-2 block"
											>
												Email title
											</label>
											<Input
												id="custom-email-title-input"
												data-testid="custom-email-title"
												value={customEmailTitle ?? ""}
												onChange={onCustomEmailTitleChange}
												className="w-full bg-white"
											/>
											{errors.customEmailTitle && (
												<p className="text-sm text-red-500 mt-1">
													{errors.customEmailTitle.message}
												</p>
											)}
										</div>

										<div>
											<label
												htmlFor="custom-email-subject-input"
												className="text-sm font-medium text-zinc-600 mb-2 block"
											>
												Email subject
											</label>
											<Input
												id="custom-email-subject-input"
												data-testid="custom-email-subject"
												value={customEmailSubject ?? ""}
												onChange={onCustomEmailSubjectChange}
												className="w-full bg-white"
											/>
											{errors.customEmailSubject && (
												<p className="text-sm text-red-500 mt-1">
													{errors.customEmailSubject.message}
												</p>
											)}
										</div>

										<div>
											<label className="text-sm font-medium text-zinc-600 mb-2 block">
												Recipient
											</label>
											<div className="flex items-center gap-3">
												<Input
													className="w-full bg-white opacity-60 cursor-not-allowed"
													disabled
													data-testid="custom-email-recipient"
													value={
														observationType === "STORE_SCORED_ASSESSMENT" ||
														observationType === "STORE_SELF_ASSESSMENT"
															? "Hi [Store Name]"
															: "Hi [First name, Last name]"
													}
													readOnly
												/>
												<div className="flex items-center gap-2 shrink-0">
													<LockIcon
														style={{ minWidth: "16px" }}
														height="16px"
													/>
													<p className="text-[13px] text-muted-foreground">
														Automatically populated for each recipient
													</p>
												</div>
											</div>
										</div>

										<div>
											<label className="text-sm font-medium text-zinc-600 mb-2 block">
												Intro text
											</label>
											<textarea
												data-testid="custom-email-body"
												className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 overflow-hidden resize-none min-h-[160px]"
												value={customEmailBody ?? ""}
												onChange={onCustomEmailBodyChange}
												onInput={(e) => {
													const target = e.target as HTMLTextAreaElement;
													target.style.height = "auto";
													target.style.height = `${target.scrollHeight}px`;
												}}
											/>
											{errors.customEmailBody && (
												<p className="text-sm text-red-500 mt-1">
													{errors.customEmailBody.message}
												</p>
											)}
											<p className="text-[13px] text-muted-foreground mt-2 text-center">
												Form answers/result will be displayed below this text
											</p>
										</div>
									</div>
								)}
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<Link
						to="/observations/forms"
						className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
					>
						<ArrowLeft className="h-4 w-4" />
						Back to forms
					</Link>

					<Button
						type="button"
						onClick={handleSubmit(onValid)}
						disabled={!isValid || !category}
					>
						<Save className="h-4 w-4" />
						Save &amp; continue
					</Button>
				</div>
			</form>
		</div>
	);
};

export { CreateNewObservationForm, type CreateNewObservationFormProps };
